import { Link } from 'react-router-dom';
import { AlertTriangle, Ban, X } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { useBillingAlerts } from '@features/billing/hooks/useBillingAlerts';
import type { BillingAlert } from '@/services/billingAlerts.service';
import { cn } from '@shared/lib/utils';

const MAX_LAYOUT_ALERTS = 3;

function AlertRow({
  alert,
  dense,
  onDismiss,
  showTenant,
}: {
  alert: BillingAlert;
  dense?: boolean;
  onDismiss: (id: string) => void;
  showTenant?: boolean;
}) {
  const exceeded = alert.severity === 'exceeded';
  const Icon = exceeded ? Ban : AlertTriangle;
  const manageHref =
    alert.scope === 'agent' && alert.agentId
      ? '/admin/agents'
      : '/admin/settings?tab=limits';

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm',
        exceeded
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : 'border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100',
      )}
      role={exceeded ? 'alert' : 'status'}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className={cn('leading-snug', dense && 'text-xs')}>
          {showTenant && alert.tenantId ? (
            <span className="mr-1 font-medium opacity-80">
              [{alert.tenantId.slice(0, 8)}…]
            </span>
          ) : null}
          {alert.message}
        </p>
        {!dense && (
          <Link
            to={manageHref}
            className="inline-block text-xs font-medium underline-offset-2 hover:underline"
          >
            Manage limits
          </Link>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 opacity-70 hover:opacity-100"
        onClick={() => onDismiss(alert.id)}
        aria-label="Dismiss alert"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/**
 * Compact limit banners for AdminLayout (B3.3).
 * Widget public copy stays in UsageLimitBanners — admin uses BE message strings.
 */
export function BillingAlertsBanner({
  mode,
  dense,
  tenantId,
  className,
}: {
  mode?: 'tenant' | 'platform';
  /** Show more alerts (billing pages). */
  dense?: boolean;
  /** Platform mode: optional tenant filter. */
  tenantId?: string | null;
  className?: string;
}) {
  const { alerts, dismiss, mode: resolvedMode } = useBillingAlerts({
    mode,
    tenantId,
  });

  if (!resolvedMode || alerts.length === 0) return null;

  const shown = dense ? alerts : alerts.slice(0, MAX_LAYOUT_ALERTS);
  const hidden = dense ? 0 : Math.max(0, alerts.length - shown.length);
  const showTenant = resolvedMode === 'platform';

  return (
    <div className={cn('space-y-2', className)}>
      {shown.map((alert) => (
        <AlertRow
          key={alert.id}
          alert={alert}
          dense={dense}
          onDismiss={dismiss}
          showTenant={showTenant}
        />
      ))}
      {hidden > 0 && (
        <p className="text-xs text-muted-foreground">
          +{hidden} more — see{' '}
          <Link
            to={
              resolvedMode === 'platform'
                ? '/admin/platform-billing'
                : '/admin/billing'
            }
            className="underline-offset-2 hover:underline"
          >
            AI Billing
          </Link>
        </p>
      )}
    </div>
  );
}
